import { type UseQueryResult, useQueries, useQuery } from '@tanstack/react-query';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { useIsFocused } from 'expo-router/react-navigation';
import { useAtomValue } from 'jotai';
import {
  Camera,
  ClipboardList,
  FileUp,
  Image as ImageIcon,
  Link as LinkIcon,
  MessageSquareText,
} from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import { AddLinkSheet } from './AddLinkSheet';
import { AttachInquirySheet, attachedInquiryChipTitle } from './AttachInquirySheet';
import { AttachSurveySheet } from './AttachSurveySheet';
import { EmptyThread } from './EmptyThread';
import { SupportAttachMenuSheet, type AttachMenuOption } from './SupportAttachMenuSheet';
import { isPreviewableImage } from './SupportMessageAttachments';
import { SupportMessageList, type SupportMessageListHandle } from './SupportMessageList';
import { SupportThreadComposer } from './SupportThreadComposer';
import { getDockPaddingBottom } from './dockPadding';
import {
  isMemberView,
  supportThreadCounterpartAvatarUrl,
  supportThreadCounterpartName,
} from './participantLabel';
import { messageBodyWithLinks } from './supportLinks';
import { buildThreadItems, type ThreadMessageItem } from './threadItems';
import { useSupportLinkOpener } from './useSupportLinkOpener';

import { Button, ButtonText } from '@/components/ui/button';
import { useLiquidGlass } from '@/components/ui/glass';
import { KeyboardSpacer } from '@/components/ui/keyboard-spacer';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import {
  clearSupportDraft,
  readSupportDraft,
  updateSupportDraftInquiry,
  updateSupportDraftText,
  useSupportDraftInquiry,
  useSupportDraftVersion,
} from '@/features/support-contact/state';
import { useMarkSupportThreadRead } from '@/hooks/conversations/useMarkSupportThreadRead';
import { useShareableSurveys } from '@/hooks/conversations/useShareableSurveys';
import { useSupportFilePicker } from '@/hooks/conversations/useSupportFilePicker';
import { useSendSupportMessage, useSupportThread } from '@/hooks/conversations/useSupportThread';
import { useColors } from '@/hooks/useColors';
import { useIsKeyboardVisible } from '@/hooks/useIsKeyboardVisible';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import {
  useCanReadUserInquiries,
  useCanShareSurvey,
  useCanStartSupportThread,
} from '@/hooks/useSupportStaffAbilities';
import { toConversationError } from '@/services/conversations/errors';
import type { InquiryForwardTarget } from '@/services/conversations/inquiryForward';
import { getLinkPreviewQueryOptions, type LinkPreview } from '@/services/conversations/linkPreview';
import {
  getMemberRecentInquiriesQueryOptions,
  type MemberInquiry,
} from '@/services/conversations/memberInquiries';
import { toReferencedInquiry } from '@/services/conversations/referencedInquiry';
import type { ShareableSurvey } from '@/services/conversations/shareableSurveys';
import { sharedSurveyRouteId, type SharedSurvey } from '@/services/conversations/sharedSurvey';
import type { MessageReference, OutgoingFile } from '@/services/conversations/thread';
import { userIdAtom } from '@/services/globalState';
import {
  trackSupportForwardInquiryOpened,
  trackSupportMessageFailed,
  trackSupportMessageSent,
  trackSupportSurveyOpened,
  trackSupportThreadOpened,
} from '@/util/analyticsEvents';
import { revokeBlobUri } from '@/util/revokeBlobUri';

/** The inquiry the next message will carry, with the title its chip shows. */
type AttachedInquiry = MessageReference & { title: string };

type SupportThreadViewProps = {
  conversationId: string;
  /** Height of a transparent header drawn over the thread, which the messages clear. */
  topInset?: number;
};

/**
 * Each link's unfurled title, or undefined while it loads. Module level so the
 * combine is one stable function, which lets the query keep the same array
 * until a title actually changes.
 */
function linkTitlesOf(results: UseQueryResult<LinkPreview | null>[]): (string | undefined)[] {
  return results.map((result) => result.data?.title);
}

/** The fade behind the floating composer, so messages dissolve rather than cut off under it. */
const COMPOSER_FADE_HEIGHT = 56;

function NotFound() {
  return (
    <VStack className="flex-1 items-center justify-center gap-3 px-6">
      <Text className="text-center text-base text-typography-900">
        This conversation isn’t available.
      </Text>
      <Button variant="outline" onPress={() => router.replace(ROUTES.INBOX)}>
        <ButtonText>Back to inbox</ButtonText>
      </Button>
    </VStack>
  );
}

function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <VStack className="flex-1 items-center justify-center gap-3 px-6">
      <Text className="text-center text-base text-typography-900" accessibilityLiveRegion="polite">
        The conversation didn’t load.
      </Text>
      <Button variant="outline" onPress={onRetry}>
        <ButtonText>Try again</ButtonText>
      </Button>
    </VStack>
  );
}

/**
 * A support thread's messages and composer, shared by the mobile route and the
 * desktop chat pane. It reads and writes only the conversations API; no
 * Twilio client is involved, so it works whether or not one exists.
 *
 * The thread is kept current by the app-wide inbox socket, by polling while
 * that socket is down, and by a refetch whenever the view regains focus. It is
 * marked read while focused.
 *
 * Where Liquid Glass is drawn the composer floats over the thread on a fade,
 * as Research's does, and the messages scroll beneath it; elsewhere it sits
 * below them. Either way the composer owns the bottom safe area, and the
 * keyboard spacer lifts the whole column, composer included.
 */
export function SupportThreadView({ conversationId, topInset = 0 }: SupportThreadViewProps) {
  const isFocused = useIsFocused();

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  const isKeyboardVisible = useIsKeyboardVisible();

  const hasGlass = useLiquidGlass();

  const isNative = Platform.OS !== 'web';

  const isDockFloating = hasGlass && isNative;

  const [dockHeight, setDockHeight] = useState(0);

  const viewerId = useAtomValue(userIdAtom);

  const isCompactLayout = useIsCompactLayout();

  const listRef = useRef<SupportMessageListHandle>(null);

  const linkOpener = useSupportLinkOpener();

  const query = useSupportThread(conversationId, { isFocused });

  const thread = query.data;

  useMarkSupportThreadRead(conversationId, thread, { isFocused });

  const { send, discard } = useSendSupportMessage(conversationId);

  /** Remounts the composer with the new text when a prompt starts a draft for this thread. */
  const draftVersion = useSupportDraftVersion(conversationId);

  const handleDraftChange = useCallback(
    (text: string) => updateSupportDraftText(conversationId, text),
    [conversationId]
  );

  const subject = thread?.meta.subject;

  const viewerRole = isMemberView(subject, viewerId) ? 'member' : 'support';

  const counterpartName = supportThreadCounterpartName(subject, viewerId);

  const canReadUserInquiries = useCanReadUserInquiries();

  const canStartSupportThread = useCanStartSupportThread();

  const memberId = subject?.id;

  /**
   * The picker is for staff. Listing a member's inquiries is also granted to
   * staff on their own account, but only Primum Support takes part in the
   * member's thread, so the attach control needs both or it offers a send that
   * is always refused. Members attach only from an inquiry's menu.
   */
  const canAttach = canReadUserInquiries && canStartSupportThread && memberId !== undefined;

  /**
   * Sharing a survey is its own ability, granted only to Primum Support and
   * only for a published survey, so the option follows the server's rule
   * rather than the inquiry picker's. Only a support thread has a member to
   * share with.
   */
  const canShareSurvey = useCanShareSurvey() && memberId !== undefined;

  const [surveySheetThreadId, setSurveySheetThreadId] = useState<string>();

  const [threadSurvey, setThreadSurvey] = useState<{
    conversationId: string;
    survey: SharedSurvey;
  }>();

  const [attachSheetThreadId, setAttachSheetThreadId] = useState<string>();

  const [attachMenuThreadId, setAttachMenuThreadId] = useState<string>();

  const [linkSheetThreadId, setLinkSheetThreadId] = useState<string>();

  const [threadLinks, setThreadLinks] = useState<{ conversationId: string; links: string[] }>();

  const [threadFiles, setThreadFiles] = useState<{
    conversationId: string;
    files: OutgoingFile[];
    noPHIConfirmed: boolean;
  }>();

  const { takePhoto, pickPhoto, pickFile } = useSupportFilePicker();

  const [threadAttachment, setThreadAttachment] = useState<{
    conversationId: string;
    inquiry: AttachedInquiry;
  }>();

  /**
   * The open sheet and the attached inquiry belong to the thread they were set
   * in, and read back only for it. A host that swaps `conversationId` without
   * remounting this view then cannot carry one member's inquiry into another
   * member's send, which the API would refuse.
   */
  const isAttachSheetOpen = attachSheetThreadId === conversationId;

  const isAttachMenuOpen = attachMenuThreadId === conversationId;

  const isSurveySheetOpen = surveySheetThreadId === conversationId;

  const isLinkSheetOpen = linkSheetThreadId === conversationId;

  const links = useMemo(
    () => (threadLinks?.conversationId === conversationId ? threadLinks.links : []),
    [threadLinks, conversationId]
  );

  /**
   * Each link starts unfurling as soon as it is added, so its chip names the
   * page while the message is still being written, and the sent message's
   * preview card draws from the same cache rather than loading again.
   */
  const linkTitles = useQueries({
    queries: links.map((url) => getLinkPreviewQueryOptions(url)),
    combine: linkTitlesOf,
  });

  const composerLinks = useMemo(
    () => links.map((url, index) => ({ url, title: linkTitles[index] })),
    [links, linkTitles]
  );

  const addLink = useCallback(
    (url: string) => {
      setThreadLinks((current) => ({
        conversationId,
        links: [...(current?.conversationId === conversationId ? current.links : []), url],
      }));

      setLinkSheetThreadId(undefined);
    },
    [conversationId]
  );

  const removeLink = useCallback(
    (index: number) =>
      setThreadLinks((current) =>
        current?.conversationId === conversationId
          ? {
              conversationId,
              links: current.links.filter((_link, linkIndex) => linkIndex !== index),
            }
          : current
      ),
    [conversationId]
  );

  const survey = threadSurvey?.conversationId === conversationId ? threadSurvey.survey : undefined;

  const pickedFiles = threadFiles?.conversationId === conversationId ? threadFiles : undefined;

  const files = useMemo(() => pickedFiles?.files ?? [], [pickedFiles]);

  const noPHIConfirmed = pickedFiles?.noPHIConfirmed ?? false;

  /** Picking a new file asks for the no-PHI confirmation again, since it covers what was attached when given. */
  const addFile = useCallback(
    (file: OutgoingFile | null) => {
      if (!file) {
        return;
      }

      setThreadFiles((current) => ({
        conversationId,
        files: [...(current?.conversationId === conversationId ? current.files : []), file],
        noPHIConfirmed: false,
      }));
    },
    [conversationId]
  );

  /** A removed file is no longer drawn, so the browser can drop the copy behind its preview. */
  const removeFile = useCallback(
    (id: string) => {
      revokeBlobUri(files.find((file) => file.id === id)?.uri);

      setThreadFiles((current) => {
        const remaining = (current?.conversationId === conversationId ? current.files : []).filter(
          (file) => file.id !== id
        );

        return {
          conversationId,
          files: remaining,
          noPHIConfirmed: remaining.length > 0 && Boolean(current?.noPHIConfirmed),
        };
      });
    },
    [conversationId, files]
  );

  const setNoPHIConfirmed = useCallback(
    (isConfirmed: boolean) =>
      setThreadFiles((current) =>
        current?.conversationId === conversationId
          ? { ...current, noPHIConfirmed: isConfirmed }
          : current
      ),
    [conversationId]
  );

  /**
   * A member never picks an inquiry here; one arrives only with a draft started
   * from that inquiry, and lives in the draft store so a remount keeps it.
   */
  const draftInquiry = useSupportDraftInquiry(conversationId);

  const pickedAttachment =
    threadAttachment?.conversationId === conversationId ? threadAttachment.inquiry : undefined;

  const attachment: AttachedInquiry | undefined =
    pickedAttachment ?? (viewerRole === 'member' ? draftInquiry : undefined);

  const setIsAttachSheetOpen = useCallback(
    (isOpen: boolean) => setAttachSheetThreadId(isOpen ? conversationId : undefined),
    [conversationId]
  );

  const setIsAttachMenuOpen = useCallback(
    (isOpen: boolean) => setAttachMenuThreadId(isOpen ? conversationId : undefined),
    [conversationId]
  );

  const setIsSurveySheetOpen = useCallback(
    (isOpen: boolean) => setSurveySheetThreadId(isOpen ? conversationId : undefined),
    [conversationId]
  );

  /**
   * What the attach menu offers. Anyone in the thread can send a photo, a
   * file or a link; attaching an inquiry is for staff, under the same rule as before.
   */
  const attachOptions = useMemo<AttachMenuOption[]>(
    () => [
      {
        key: 'camera',
        label: 'Camera',
        hint: 'Take a photo to send',
        icon: Camera,
        placement: 'tile',
        onSelect: () => takePhoto(files).then(addFile),
      },
      {
        key: 'photos',
        label: 'Photos',
        hint: 'Choose an image from your library',
        icon: ImageIcon,
        placement: 'tile',
        onSelect: () => pickPhoto(files).then(addFile),
      },
      {
        key: 'files',
        label: 'Files',
        hint: 'Choose a PDF, Word, Excel, CSV or video file',
        icon: FileUp,
        placement: 'tile',
        onSelect: () => pickFile(files).then(addFile),
      },
      {
        key: 'link',
        label: 'Add a link',
        hint: 'Paste the address of an article, trial or web page',
        icon: LinkIcon,
        placement: 'row',
        opensMore: true,
        onSelect: () => setLinkSheetThreadId(conversationId),
      },
      ...(canAttach
        ? [
            {
              key: 'inquiry',
              label: 'Attach an inquiry',
              hint: `Choose one of ${counterpartName}’s recent inquiries`,
              icon: MessageSquareText,
              placement: 'row' as const,
              opensMore: true,
              onSelect: () => setIsAttachSheetOpen(true),
            },
          ]
        : []),
      ...(canShareSurvey
        ? [
            {
              key: 'survey',
              label: 'Share a survey',
              hint: `Choose a published survey for ${counterpartName} to take`,
              icon: ClipboardList,
              placement: 'row' as const,
              opensMore: true,
              onSelect: () => setIsSurveySheetOpen(true),
            },
          ]
        : []),
    ],
    [
      addFile,
      canAttach,
      canShareSurvey,
      conversationId,
      counterpartName,
      files,
      pickFile,
      pickPhoto,
      setIsAttachSheetOpen,
      setIsSurveySheetOpen,
      takePhoto,
    ]
  );

  const setAttachment = useCallback(
    (inquiry: AttachedInquiry | undefined) => {
      setThreadAttachment(inquiry ? { conversationId, inquiry } : undefined);
    },
    [conversationId]
  );

  const removeAttachment = useCallback(() => {
    setAttachment(undefined);

    if (viewerRole === 'member') {
      updateSupportDraftInquiry(conversationId, undefined);
    }
  }, [setAttachment, viewerRole, conversationId]);

  const shareableSurveys = useShareableSurveys({
    enabled: canShareSurvey && isSurveySheetOpen,
  });

  const memberInquiries = useQuery({
    ...getMemberRecentInquiriesQueryOptions(memberId ?? 0),
    enabled: canAttach && isAttachSheetOpen,
  });

  const items = useMemo(() => buildThreadItems(thread, viewerId), [thread, viewerId]);

  const hasLoaded = Boolean(thread);

  const messageCount = thread?.messages.length ?? 0;

  const openedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasLoaded || openedRef.current === conversationId) {
      return;
    }

    openedRef.current = conversationId;

    trackSupportThreadOpened({
      conversation_id: conversationId,
      viewer_role: viewerRole,
      message_count: messageCount,
    });
  }, [conversationId, hasLoaded, messageCount, viewerRole]);

  const sendMessage = useCallback(
    (
      messageId: string,
      body: string,
      isRetry: boolean,
      reference?: MessageReference,
      outgoingFiles?: OutgoingFile[],
      sharedSurvey?: SharedSurvey
    ) =>
      send(
        { messageId, body, reference, survey: sharedSurvey, files: outgoingFiles },
        {
          onSuccess: () =>
            trackSupportMessageSent({
              conversation_id: conversationId,
              message_id: messageId,
              text_length: body.length,
              is_retry: isRetry,
              viewer_role: viewerRole,
              has_reference: Boolean(reference),
              has_survey: Boolean(sharedSurvey),
              file_count: outgoingFiles?.length ?? 0,
            }),
          onError: (error) =>
            trackSupportMessageFailed({
              conversation_id: conversationId,
              message_id: messageId,
              error_code: error.kind,
            }),
        }
      ),
    [conversationId, send, viewerRole]
  );

  const handleSend = useCallback(
    (body: string) => {
      sendMessage(
        uuidv4(),
        messageBodyWithLinks(body, links),
        false,
        attachment && {
          inquiryId: attachment.inquiryId,
          conversationId: attachment.conversationId,
          details: attachment.details,
        },
        files.length > 0 ? files : undefined,
        survey
      );

      setAttachment(undefined);

      setThreadSurvey(undefined);

      setThreadFiles(undefined);

      setThreadLinks(undefined);

      clearSupportDraft(conversationId);

      listRef.current?.scrollToLatest();
    },
    [sendMessage, attachment, files, links, survey, setAttachment, conversationId]
  );

  const handleRetry = useCallback(
    (message: ThreadMessageItem) =>
      sendMessage(
        message.id,
        message.body,
        true,
        message.reference,
        message.outgoingFiles,
        message.survey
      ),
    [sendMessage]
  );

  const handleAttachInquiry = useCallback(
    (inquiry: MemberInquiry) => {
      const details = toReferencedInquiry(inquiry);

      setAttachment({
        inquiryId: inquiry.id,
        conversationId: inquiry.conversationId,
        title: attachedInquiryChipTitle(details),
        details: details ?? undefined,
      });

      setIsAttachSheetOpen(false);
    },
    [setAttachment, setIsAttachSheetOpen]
  );

  /** Keeps only what the card shows, so the pending bubble matches the stored one. */
  const handleShareSurvey = useCallback(
    ({ surveyGroupId, slug, name, type }: ShareableSurvey) => {
      setThreadSurvey({ conversationId, survey: { surveyGroupId, slug, name, type } });

      setIsSurveySheetOpen(false);
    },
    [conversationId, setIsSurveySheetOpen]
  );

  /** Opens the shared survey's current version, the same way for either side of the thread. */
  const handleOpenSurvey = useCallback(
    (message: ThreadMessageItem) => {
      const shared = message.sharedSurvey;

      if (!shared) {
        return;
      }

      trackSupportSurveyOpened({
        conversation_id: conversationId,
        message_id: message.id,
        survey_group_id: shared.surveyGroupId,
        viewer_role: viewerRole,
      });

      router.push(ROUTES.SURVEY(sharedSurveyRouteId(shared)));
    },
    [conversationId, viewerRole]
  );

  const handleDiscard = useCallback((message: ThreadMessageItem) => discard(message.id), [discard]);

  const handleOpenReference = useCallback(
    (referenceId: string) => {
      if (isCompactLayout) {
        router.push({ pathname: '/inbox/chat/[id]', params: { id: referenceId } });

        return;
      }

      router.setParams({ id: referenceId });
    },
    [isCompactLayout]
  );

  const handleOpenForwardInquiry = useCallback(
    (message: ThreadMessageItem, target: InquiryForwardTarget) => {
      const forward = message.inquiryForward;

      if (!forward) {
        return;
      }

      const inquiry = target === 'new' ? forward.newInquiry : forward.previousInquiry;

      trackSupportForwardInquiryOpened({
        conversation_id: conversationId,
        message_id: message.id,
        inquiry_id: inquiry.id,
        target,
        viewer_role: viewerRole,
      });

      handleOpenReference(inquiry.conversationId);
    },
    [conversationId, handleOpenReference, viewerRole]
  );

  if (query.isPending) {
    return (
      <VStack className="flex-1 items-center justify-center">
        <Spinner size="large" accessibilityLabel="Loading conversation" />
      </VStack>
    );
  }

  if (query.isError && !thread) {
    return toConversationError(query.error).kind === 'not_found' ? (
      <NotFound />
    ) : (
      <LoadError onRetry={() => query.refetch().catch(() => undefined)} />
    );
  }

  const dockPaddingBottom = getDockPaddingBottom({
    isNative,
    isKeyboardVisible,
    isDockFloating,
    bottomInset: bottom,
  });

  return (
    <VStack className="flex-1">
      <VStack className="relative min-h-0 flex-1">
        <SupportMessageList
          ref={listRef}
          items={items}
          counterpartName={counterpartName}
          counterpartAvatarUrl={supportThreadCounterpartAvatarUrl(subject, viewerId)}
          topInset={topInset}
          bottomInset={isDockFloating ? dockHeight : 0}
          onRetry={handleRetry}
          onDiscard={handleDiscard}
          onOpenReference={handleOpenReference}
          onOpenForwardInquiry={handleOpenForwardInquiry}
          onOpenSurvey={handleOpenSurvey}
          onOpenLink={linkOpener.openLink}
          emptyState={<EmptyThread viewerRole={viewerRole} counterpartName={counterpartName} />}
        />

        <View
          className={twJoin(isDockFloating && 'absolute bottom-0 left-0 right-0')}
          onLayout={
            isDockFloating ? (event) => setDockHeight(event.nativeEvent.layout.height) : undefined
          }>
          {isDockFloating ? (
            <LinearGradient
              colors={[`${colors.background[50]}00`, colors.background[50]]}
              pointerEvents="none"
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: COMPOSER_FADE_HEIGHT * 2,
              }}
            />
          ) : null}

          <View className="pt-2" style={{ paddingBottom: dockPaddingBottom }}>
            <SupportThreadComposer
              onSend={handleSend}
              onAttach={() => setIsAttachMenuOpen(true)}
              attachment={attachment}
              onRemoveAttachment={removeAttachment}
              survey={survey}
              onRemoveSurvey={() => setThreadSurvey(undefined)}
              files={files.map((file) => ({
                id: file.id,
                name: file.name,
                isImage: isPreviewableImage(file.mimeType),
              }))}
              onRemoveFile={removeFile}
              links={composerLinks}
              onRemoveLink={removeLink}
              noPHIConfirmed={noPHIConfirmed}
              onNoPHIConfirmedChange={setNoPHIConfirmed}
              key={draftVersion}
              initialText={readSupportDraft(conversationId)}
              onTextChange={handleDraftChange}
            />
          </View>
        </View>
      </VStack>

      {isNative ? <KeyboardSpacer /> : null}

      {linkOpener.sheet}

      <SupportAttachMenuSheet
        isOpen={isAttachMenuOpen}
        onClose={() => setIsAttachMenuOpen(false)}
        options={attachOptions}
      />

      <AddLinkSheet
        isOpen={isLinkSheetOpen}
        onClose={() => setLinkSheetThreadId(undefined)}
        onAdd={addLink}
      />

      {canAttach ? (
        <AttachInquirySheet
          isOpen={isAttachSheetOpen}
          onClose={() => setIsAttachSheetOpen(false)}
          memberName={counterpartName}
          inquiries={memberInquiries.data}
          isLoading={memberInquiries.isLoading}
          isError={memberInquiries.isError}
          onRetry={() => memberInquiries.refetch().catch(() => undefined)}
          onSelect={handleAttachInquiry}
        />
      ) : null}

      {canShareSurvey ? (
        <AttachSurveySheet
          isOpen={isSurveySheetOpen}
          onClose={() => setIsSurveySheetOpen(false)}
          memberName={counterpartName}
          surveys={shareableSurveys.surveys}
          isLoading={shareableSurveys.isLoading}
          isError={shareableSurveys.isError}
          onRetry={() => shareableSurveys.refetch().catch(() => undefined)}
          onSelect={handleShareSurvey}
        />
      ) : null}
    </VStack>
  );
}
